import { FolderIcon, FolderOpen } from "lucide-react";
import { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { UseTreeNavigationResult } from "../types";

import { getEntityIconColor } from "@/components/UnifiedRegistry/utils";
import { EntityIcon } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { Entity, EntityType } from "@/queries/folder-entities/types";
import { routePaths } from "@/utils/routePaths";
import { withPreservedWorkspaceSearch } from "@/utils/requestLogUrlParams";

export const useTreeNavigation = (): UseTreeNavigationResult => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const getItemPath = useCallback(
    (item: Entity) => routePaths.getEntityPath(params.workspaceId, item.type, item.id),
    [params.workspaceId],
  );

  const isCurrentPath = useCallback(
    (itemPath: string) =>
      location.pathname === itemPath || location.pathname.startsWith(itemPath + "/"),
    [location.pathname],
  );

  const getIcon = (item: Entity, isCollapsed = true) => {
    if (item.type === EntityType.FOLDER) {
      const iconClass = "h-3.5 w-3.5 flex-shrink-0";
      const colorClass = getEntityIconColor(item.type);
      return isCollapsed ? (
        <FolderIcon className={cn(iconClass, colorClass)} />
      ) : (
        <FolderOpen className={cn(iconClass, colorClass)} />
      );
    }

    return <EntityIcon type={item.type} size="sm" />;
  };

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(withPreservedWorkspaceSearch(path, location.search));
    },
    [navigate, location.search],
  );

  return {
    navigate: handleNavigate,
    getItemPath,
    isCurrentPath,
    getItemIcon: getIcon,
  };
};
